import { useEffect, useState } from "react";
import { getToken } from "../../../services/auth";
import { Helmet } from "react-helmet-async";
import { useNavigate, useSearchParams } from "react-router-dom";
import Loader from "../../../components/modal/Loader";
import Button from "../../../components/button/Button";
import Select from "../../../components/select/Select";
import InputText from "../../../components/inputText/InputText";
import Pagination from "../../../components/pagination/Pagination";
import { config } from '../../../config/env';

const fetchOverviews = async ({
  page = 1,
  limit = 10,
  sort_By = "created_at",
  order_by = "desc",
  search,
  is_active,
}) => {
  try {
    const params = new URLSearchParams();
    params.append('page', page);
    params.append('limit', limit);
    params.append('sort_by', sort_By);
    params.append('order_by', order_by);
    if (search) {
      params.append('search', search);
    }
    if (is_active) {
      params.append('is_active', is_active);
    }

    const response = await fetch(
    `${config.apiBaseUrl}/api/v1/admin/overview/?${params.toString()}`,
    {
        method: "GET",
        headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${await getToken()}`,
        },
    }
    );
    if (!response.ok) {
      throw new Error('Network response was not ok');
    }
    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error fetching contents:', error);
    throw error;
  }
}

const Overviews = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();

    const defaultState = {
        filter: {
            search: "",
            is_active: "",
            page: 1,
            limit: 10,
            sort_By: "created_at",
            order_by: "desc",
        },
        data: [],
        total: 0,
        total_by_filter: 0,
        is_loading: false,
        selectedStatus: "",
    };

    const statusList = [
      { id: "",  name: "All" },
      { id: "1", name: "Active" },
      { id: "0", name: "Inactive" },
    ];

    const [state, setState] = useState(defaultState);
    const [resetCount, setResetCount] = useState(0);

    const updateQuery = (pairs) => {
      if (pairs.length === 0) {
        navigate("", { replace: true });
        return;
      };

      const newSearchParams = new URLSearchParams(searchParams.toString());
      pairs.forEach((element) => {
        newSearchParams.set(element[0], element[1]);
      });
      navigate(`?${newSearchParams.toString()}`, { replace: true });
    };

    const loadOverviews = async () => {
      setState((prevState) => ({
          ...prevState,
          is_loading: true,
      }));
      const overviewData = await fetchOverviews({
            page: state.filter.page,
            limit: state.filter.limit,
            sort_By: state.filter.sort_By,
            order_by: state.filter.order_by,
            search: state.filter.search,
            is_active: state.filter.is_active,
      });
      setState((prevState) => ({
          ...prevState,
          data: overviewData.data,
          total: overviewData.total,
          total_by_filter: overviewData.count,
          is_loading: false,
      }));
    }

    const addOverviewHandler = () => {
      navigate("/ava-admin/overview/add");
    };

    const statusChangeHandler = (selectedItem) => {
      setState((prevState) => ({
        ...prevState,
        filter: {
          ...prevState.filter,
          is_active: selectedItem.id,
        },
        selectedStatus: selectedItem.id,
      }));
    };

    const filterHandler = async () => {
      console.log("Filter applied:" );
      setState((prevState) => ({
        ...prevState,
        filter: {
          ...prevState.filter,
          page: 1,
        },
      }));
      let pairs = [
        ['page', 1],
      ];
      if (state.filter.is_active) {
        pairs.push(['is_active', state.filter.is_active]);
      } else {
        pairs.push(['is_active', '']);
      }
      if (state.filter.search) {
        pairs.push(['search', state.filter.search]);
      } else {
        pairs.push(['search', '']);
      }
      updateQuery(pairs);
      setResetCount((prevCount) => prevCount + 1);
    };

    const resetFilterHandler = async () => {
      console.log("Resetting filters");
      setState((prevState) => ({
        ...prevState,
        filter: {
          ...defaultState.filter,
        },
        selectedStatus: "",
      }));
      updateQuery([]);
      setResetCount((prevCount) => prevCount + 1);
    };

    const pageChangeHandler = async (newPage) => {
      console.log("Page changed:", newPage);
      setState((prevState) => ({
        ...prevState,
        filter: {
          ...prevState.filter,
          page: newPage,
        },
      }));
      updateQuery([['page', newPage]]);
      setResetCount((prevCount) => prevCount + 1);
    };

    useEffect(() => {
      console.log(searchParams.toString());
      // Check if there are any search params and set state accordingly
      if (searchParams.has('is_active') || searchParams.has('search') || searchParams.has('page')) {
        setState((prevState) => ({
          ...prevState,
          filter: {
            ...prevState.filter,
            is_active: searchParams.get('is_active') || "",
            search: searchParams.get('search') || "",
            page: parseInt(searchParams.get('page')) || 1,
          },
          selectedStatus: searchParams.get('is_active') || "",
        }));
      }
      setResetCount((prevCount) => prevCount + 1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
      if (resetCount === 0) return;

      const initialized = async () => {
        await loadOverviews();
      }
      initialized();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [resetCount]);

    const renderList = () => {
      if (!state.data || state.data.length === 0) {
        return (
          <div className="flex justify-center items-center h-[200px]">
            <div className="text-gray-500 text-lg font-semibold">
              No overviews found.
            </div>
          </div>
        );
      }
      
      return (
        <div className="mt-8 flow-root">
          <div className="-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
            <div className="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8">
              <table className="relative min-w-full divide-y divide-gray-300">
                <thead>
                  <tr>
                    <th scope="col" className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-0">
                      Description
                    </th>
                    <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                      Status
                    </th>
                    <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                      Created
                    </th>
                    <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                      Created By
                    </th>
                    <th scope="col" className="py-3.5 pl-3 pr-4 sm:pr-0">
                      <span className="sr-only">Edit</span>
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {state.data.map((item) => (
                    <tr key={item.id}>
                      <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900 sm:pl-0">
                        {item.description}
                      </td>
                      <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">{item.is_active ? 'Active' : 'Inactive'}</td>
                      <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">{new Date(item.created_at).toLocaleDateString("en-US", {
                        year: "numeric",
                        month: "long",
                        day: "2-digit",
                      })}</td>
                      <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">{item.created_by}
                      </td>
                      <td className="whitespace-nowrap py-4 pl-3 pr-4 text-right text-sm font-medium sm:pr-0">
                        <a href={`/ava-admin/overview/update/${item.id}`} className="text-indigo-600 hover:text-indigo-900 px-5">
                          Edit<span className="sr-only">, {item.id} </span>
                        </a>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )
    }

    return (
      <>
        <Helmet>
          <title>Avanade Research - Admin</title>
        </Helmet>
        <div page="overviews">
          <div className="w-[calc(100%-40px)] min-h-[calc(100%-40px)] m-[20px] bg-white rounded-2xl p-10 shadow-lg text-sm">
            <div className="max-w-[1280px] mx-auto">
              <div className="flex w-full">
                <div className="block w-full">
                  <div className="text-lg font-semibold">Overviews</div>
                  <div className="text-gray-600">
                    Manage overview content for the platform.
                  </div>
                </div>
                <div className="content-center">
                  <div className="text-nowrap">
                    <Button
                      buttonClassName="py-2 px-5"
                      onClick={addOverviewHandler}
                    >
                      Add Overview
                    </Button>
                  </div>
                </div>
              </div>

              <div className="mt-10">
                <div className="flex font-semibold mb-10">
                  <div className="w-[15%] mr-5">
                    <Select
                      items={statusList}
                      label="Status"
                      itemChangeHandler={statusChangeHandler}
                      selectedItemId={state.selectedStatus}
                    />
                  </div>
                  <div className="w-[30%]">
                    <InputText
                      label="Search"
                      value={state.filter.search}
                      onBlur={(e) => setState({ ...state, filter: { ...state.filter, search: e.target.value } })}
                      onKeyDown={(e) =>
                        e.key === "Enter" && document.activeElement.blur()
                      }
                      onChange={(e) => setState({ ...state, filter: { ...state.filter, search: e.target.value } })}
                    />
                  </div>
                  <div className="flex items-center gap-12 ml-5">
                    <div className="w-[10%]">
                      <Button
                        className="mt-7"
                        buttonClassName="py-2 px-5"
                        onClick={filterHandler}>
                        Filter
                      </Button>
                    </div>
                    <div className="w-[10%] ml-5">
                      <Button
                        className="mt-7"
                        buttonClassName="py-2 px-5"
                        onClick={resetFilterHandler}>
                        Clear
                      </Button>
                    </div>
                  </div>
                </div>
              </div>

              { renderList() }

              <Pagination
                currentPage={state.filter.page}
                totalItems={state.total_by_filter}
                itemsPerPage={state.filter.limit}
                setCurrentPage={pageChangeHandler} />
              
            </div>
          </div>

          {state.is_loading && <Loader />}
        </div>
      </>
    );
}

export default Overviews;